/**
 *
 * Parameter sync middleware
 *
 * Diffs are computed against the last successfully-sent state (baseline), not per-action. This ensures that when the throttle drops intermediate diffs, the trailing diff still captures all changes since the last send.
 *
 */
import { createListenerMiddleware } from '@reduxjs/toolkit'
import { throttle } from 'throttle-debounce'
import { automationOverlay, currentSamples, getEngine, isSamplesOnWire, onAutomationSample } from '~/lib/automation'
import { isAutoplayWrite } from '~/lib/autoplayWrites'
import buildWirePatch, { sameUnit } from '~/lib/buildWirePatch'
import { DC_MESSAGES, PARAM_SEND_MS } from '~/lib/constants'
import paramChannel from '~/lib/paramChannel'
import socket from '~/lib/socket'
import { liveActions, patchLiveObject, patchModulators, setLiveProperties, setLiveProperty } from '~/store/live'
import { selectLive } from '~/store/live/selectors'
import { selectPlayback } from '~/store/playback/selectors'
import { setSettingsProperty } from '~/store/settings'
import { selectSettings } from '~/store/settings/selectors'
import { setMouseDown } from '~/store/ui'
import { SERVER_SETTING_KEYS, serverSettingsFrom } from '~/lib/serverSettings'
import { WIRE_KEYS } from '~/schemas/effect_schemas'

// The wire boundary is a pure, socket/store-free module — importing THIS file pulls in socket.js and the whole store graph. Re-exported because socket.js builds the `is_default` snapshot from the same function.
export { default as buildWirePatch } from '~/lib/buildWirePatch'

// Track transition state to reduce logging
let isTransitioning = false

// Baseline: the live state the server already knows about. Updated after each successful send and when receiving sync from server.
let lastSentLive = null

const NO_SAMPLES = Object.freeze({})

/**
 * Overlay the channel's effect params onto `live`.
 *
 * ⚠️ THE ONE READ FOR EVERY SENDER. The channel carries mid-flight values Redux has not been told about, so a sender reading raw `live` diffs pre-fade values against a mid-flight baseline and puts a full revert on the wire.
 *
 * ⚠️ An effect section comes from the channel WHOLE: absence there means at-initial, so merging a stale Redux key back re-asserts a value the fade already swept away. Redux still governs EXISTENCE.
 */
export const withChannelParams = live => {
	const snapshot = paramChannel.snapshot()
	// Loop samples ride on top for a server that takes no `samples` message; the channel never holds one (automation plan A10).
	const auto = isSamplesOnWire() ? NO_SAMPLES : automationOverlay()
	const out = {}
	if (live.modulators) out.modulators = live.modulators
	for (const key of WIRE_KEYS) {
		if (key === 'objects') {
			if (live[key] !== undefined) out[key] = live[key]
			continue
		}
		// ⚠️ EXISTENCE is Redux's OR the channel's. A fade writes only the channel, so an effect it is fading in exists before Redux is told; gating on `live` alone drops it from every send for the whole sweep — and from the baseline, which is what would later read its arrival as a change nobody made.
		if (live[key] === undefined && snapshot[key] === undefined && !auto[key]) continue
		out[key] = auto[key] ? { ...snapshot[key], ...auto[key] } : (snapshot[key] ?? {})
	}
	return out
}

// From DC open until the join snapshot is merged, nothing sends.
let holding = false
// What changed while the DC was down or held: section → the keys in it (an effect's params, `objects` ids, `modulators` paths). A `skipSync` write is in neither.
let gapEdits = new Map()
let gapAutoplay = new Map()
// The merge's verdict, sent once the snapshot has been applied.
let merged = null

const keysOf = (...sections) => new Set(sections.flatMap(s => (s && typeof s === 'object' ? Object.keys(s) : [])))

const noteGapEdit = (into, was, now) => {
	for (const section of keysOf(was, now)) {
		if (was?.[section] === now?.[section]) continue
		const ids = into.get(section) ?? new Set()
		for (const id of keysOf(was?.[section], now?.[section]))
			if (was?.[section]?.[id] !== now?.[section]?.[id]) ids.add(id)
		into.set(section, ids)
	}
}

const pick = (dif, kept) => {
	const out = {}
	for (const section in kept) {
		if (!(section in dif)) continue
		if (dif[section] === null) {
			out[section] = null
			continue
		}
		const part = {}
		for (const id of kept[section]) if (id in dif[section]) part[id] = dif[section][id]
		if (Object.keys(part).length) out[section] = part
	}
	return out
}

const doSendLiveSync = live => {
	if (!lastSentLive || holding) return
	if (socket.dc?.readyState !== 'open') return
	const dif = buildWirePatch(lastSentLive, live)
	if (Object.keys(dif).length === 0) return
	socket.sendDC('parameters', dif)
	lastSentLive = live
}

// ⚠️ Takes a reader, never a snapshot: a trailing call's snapshot predates inbound frames and echoes them back.
const sendLiveSync = throttle(PARAM_SEND_MS, read => doSendLiveSync(read()))

export const flushLiveSync = live => doSendLiveSync(live)

/** DC open: hold every send until the join snapshot is merged. */
export const holdLiveSync = () => {
	holding = true
}

export const isLiveSyncHeld = () => holding

/** Three-way per unit (base = last sent, mine = changed here, server = snapshot): mine alone is kept and stripped from the snapshot, both changed → the server's; returns how many of the user's own edits were overridden. On the runner autoplay's gap writes merge too, but never count. */
export const mergeSnapshot = (payload, modulators, live, runner) => {
	if (!holding || !lastSentLive) return 0
	const base = lastSentLive
	const mine = withChannelParams(live)
	const server = { ...payload, modulators }
	const kept = {}
	let conflicts = 0
	for (const section of keysOf(base, mine)) {
		const edited = gapEdits.get(section)
		const autoplay = runner ? gapAutoplay.get(section) : undefined
		for (const id of keysOf(base[section], mine[section])) {
			if (!edited?.has(id) && !autoplay?.has(id)) continue
			if (sameUnit(section, id, base[section], mine[section])) continue
			if (!sameUnit(section, id, base[section], server[section])) {
				if (edited?.has(id) && !sameUnit(section, id, mine[section], server[section])) conflicts++
				// A snapshot omits a deleted object rather than nulling it.
				if (section === 'objects' && server.objects?.[id] === undefined && payload.objects)
					payload.objects[id] = null
				continue
			}
			;(kept[section] ??= new Set()).add(id)
			if (server[section] && typeof server[section] === 'object') delete server[section][id]
		}
	}
	merged = { base, mine, kept }
	return conflicts
}

/** After the snapshot is applied, or on a fresh join with nothing to merge: send what the merge kept, then resume. */
export const releaseLiveSync = live => {
	if (!holding) return
	holding = false
	gapEdits = new Map()
	gapAutoplay = new Map()
	if (!merged) return doSendLiveSync(withChannelParams(live))
	const { base, mine, kept } = merged
	merged = null
	const dif = pick(buildWirePatch(base, mine), kept)
	if (Object.keys(dif).length && socket.dc?.readyState === 'open') socket.sendDC('parameters', dif)
	lastSentLive = withChannelParams(live)
}

/**
 * Mark what the CHANNEL now holds as already known to the server.
 *
 * ⚠️ What `meta.sync` does for a dispatch, for a writer with no action to carry it — a peer's values echo straight back without it.
 *
 * ⚠️ Through the overlay, never raw `live`: an inbound value is in the channel before it settles, and a baseline read from Redux alone would omit it and send it back.
 */
export const syncWireBaseline = (live, effectKeys) => {
	const next = withChannelParams(live)
	// ⚠️ A whole-baseline REPLACE marks every key already-sent, including a local edit made inside the 33ms throttle window — the trailing send then diffs to empty and the edit is lost for good. With `effectKeys` only what the inbound payload actually named is marked, so an untouched local edit still ships.
	if (!lastSentLive || !effectKeys?.length) {
		lastSentLive = next
		return
	}
	const merged = { ...lastSentLive }
	for (const key of effectKeys) {
		if (next[key] === undefined) delete merged[key]
		else merged[key] = next[key]
	}
	lastSentLive = merged
}

/**
 * The effect names one `meta.sync` action touched, or `undefined` for a whole-state replace.
 *
 * ⚠️ `undefined` means REPLACE THE WHOLE BASELINE, and only `setLiveObject` may mean it. An action whose shape is not recognized falls through to the same full replace rather than silently scoping to nothing.
 */
const syncedEffects = action => {
	const type = action.type
	if (type === setLiveProperty.type) return [String(action.payload?.[0] ?? '').split('.')[0]]
	if (type === setLiveProperties.type) return (action.payload ?? []).map(([path]) => String(path).split('.')[0])
	if (type === patchLiveObject.type) return Object.keys(action.payload ?? {})
	if (type === patchModulators.type) return ['modulators']
	return undefined
}

const parameterSync = createListenerMiddleware()
parameterSync.startListening({
	matcher: liveActions,
	effect: async (action, { getState, getOriginalState }) => {
		if (action.meta?.sync) {
			// Server sent this state — update baseline so we don't echo it back.
			//
			// ⚠️ SCOPED to what the action named, the same reason `inboundParams` scopes its own call: a whole replace marks a concurrent local edit as already-sent and the trailing send drops it. A sync `setLiveObject` is the exception and keeps the full replace — the server sent the whole state, so a whole-baseline reset is what it means.
			syncWireBaseline(selectLive(getState()), syncedEffects(action))
			return
		}

		if (action.meta?.skipSync) {
			// Preset loaded for display during session reconnect — don't sync to server. The server will provide the authoritative state via data channel.
			return
		}

		// Initialize baseline to the pre-action state (what the server already knows from the initial sync / is_default handshake)
		if (!lastSentLive) {
			lastSentLive = withChannelParams(selectLive(getOriginalState()))
		}

		if (holding || socket.dc?.readyState !== 'open')
			noteGapEdit(
				isAutoplayWrite() ? gapAutoplay : gapEdits,
				selectLive(getOriginalState()),
				selectLive(getState())
			)

		const s = getState()

		// Track transition state
		const playback = selectPlayback(s)
		const isInTransition = playback.next_preset_index >= 0
		if (isInTransition && !isTransitioning) isTransitioning = true
		else if (!isInTransition && isTransitioning) isTransitioning = false

		sendLiveSync(() => withChannelParams(selectLive(getState())))
	},
})

// The runner's whole sample set; one empty set after the last loop ends, or the engine stops, clears the server's overlay.
let samplesSent = false
const doSendSamples = () => {
	if (socket.dc?.readyState !== 'open') return
	const samples = getEngine() ? currentSamples() : new Map()
	if (!samples.size && !samplesSent) return
	socket.sendDC(DC_MESSAGES.SAMPLES, Object.fromEntries(samples))
	samplesSent = samples.size > 0
}
const sendSamples = throttle(PARAM_SEND_MS, doSendSamples)

let unobserveFlush = null
let unobserveAutomation = null
/**
 * Put every channel-only write on the wire — a running fade, and equally a gesture in flight.
 *
 * ⚠️ THE CHANNEL'S OWN FLUSH IS THE CLOCK, not any action: neither a fade nor a transient gesture dispatches anything `liveActions` matches.
 */
export const observeChannelFlush = getState => {
	unobserveFlush?.()
	unobserveAutomation?.()
	const send = () => {
		if (!lastSentLive) return
		sendLiveSync(() => withChannelParams(selectLive(getState())))
	}
	unobserveFlush = paramChannel.observe(send)
	// A loop moves without any channel write or action, so its own tick is a send trigger too.
	unobserveAutomation = onAutomationSample(() => {
		if (!isSamplesOnWire()) send()
		else sendSamples()
	})
	return () => {
		unobserveFlush()
		unobserveAutomation()
	}
}

/**
 * Drain the params a peer sent mid-drag.
 *
 * ⚠️ Dispatched, never merged from `setMouseDown`'s reducer — a reducer-side merge is invisible to every listener, so the channel mirror misses it and the wire baseline never advances, echoing the peer's own values back at it.
 */
parameterSync.startListening({
	actionCreator: setMouseDown,
	effect: async ({ payload }, { dispatch }) => {
		if (payload) return
		const params = window.pending_params
		const modulators = window.pending_modulators
		window.pending_params = window.pending_modulators = null
		if (params) dispatch({ type: 'live/patchLiveObject', payload: params, meta: { sync: true, skipHistory: true } })
		// After the params, as on arrival.
		if (modulators)
			dispatch({ type: patchModulators.type, payload: modulators, meta: { sync: true, skipHistory: true } })
	},
})

// Listen for settings changes and send the server-relevant ones
parameterSync.startListening({
	actionCreator: setSettingsProperty,
	effect: async (action, { getState }) => {
		// Don't re-send settings that came from sync
		if (action.meta?.sync) return

		const [key] = action.payload
		if (!SERVER_SETTING_KEYS.includes(key)) return

		socket.debounceDC('parameters', {
			server_settings: serverSettingsFrom(selectSettings(getState())),
		})
	},
})

export default parameterSync
